import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { media } from '@myrealtrip/modash';

import Tab from '~components/tab';
import SwitchCase from '~components/SwitchCase';
import { TRANSPORTATION_ORIGIN } from '~config/publicEnv';
import { HEADER_Z_INDEX, PC_SEARCH_HOME_SCROLL_PADDING_TOP } from '~constants/common';
import {
  JOURNEY_TYPE,
  RAIL_EUROPE_TRAIN_CATEGORY_TYPE,
  RAIL_EUROPE_TAB_HEIGHT,
} from '~constants/pointToPointTicket';
import { WeblogPageCategory, WeblogScreenName, WeblogEventName } from '~constants/weblog';
import usePageViewLog from '~hooks/usePageViewLog';
import { usePrevious } from '~hooks/usePrevious';
import useDetectSticky from '~hooks/useDetectSticky';
import replaceSearchQueries from '~utils/pointToPointTicket/replaceSearchQueries';

import type {
  JourneyType,
  RailEuropeTrainCategoryType,
  OptionalRailEuropeTrainQueryParam,
} from '~types/queryParam';

import SearchHeader from './SearchHeader';
import SearchCategoryTab from './SearchCategoryTab';
import TicketTabContent from './TicketTabContent';
import PassTabContent from './PassTabContent';

interface Props {
  queries: OptionalRailEuropeTrainQueryParam;
}

const isCategoryType = (value: string): value is RailEuropeTrainCategoryType => {
  return Object.values(RAIL_EUROPE_TRAIN_CATEGORY_TYPE).includes(
    value as RailEuropeTrainCategoryType,
  );
};

const isJourneyType = (value: string): value is JourneyType => {
  return Object.values(JOURNEY_TYPE).includes(value as JourneyType);
};

const RailEuropeSearch = ({ queries }: Props) => {
  const { category: categoryQuery, journey: journeyQuery } = queries;

  const navigationContainerRef = useRef<HTMLDivElement>(null);
  const { isStuck } = useDetectSticky({ ref: navigationContainerRef, positionTop: 0 });

  const { pathname, query: originQuery, replace } = useRouter();

  const getDefaultCategory = () => {
    if (categoryQuery && isCategoryType(categoryQuery)) {
      return categoryQuery;
    }
    return RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET;
  };

  const [selectedCategory, setSelectedCategory] = useState<RailEuropeTrainCategoryType>(
    getDefaultCategory(),
  );

  const getDefaultJourney = () => {
    if (categoryQuery === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.PASS) {
      return null;
    }
    if (
      journeyQuery &&
      isJourneyType(journeyQuery) &&
      selectedCategory === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET
    ) {
      return journeyQuery;
    }
    if (selectedCategory === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET) {
      return JOURNEY_TYPE.ONE_WAY;
    }
    return null;
  };

  const [selectedJourney, setSelectedJourney] = useState<JourneyType | null>(getDefaultJourney());
  const prevSelectedJourney = usePrevious<JourneyType | null>(selectedJourney);

  const logItem = {
    pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
    screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
    eventName: WeblogEventName.EUROPE_TRAIN_TICKET_MAIN,

    url: `${TRANSPORTATION_ORIGIN}${pathname}`,
    itemName: selectedCategory.toUpperCase(),
    ...(selectedCategory === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET && {
      ticketType: selectedJourney === JOURNEY_TYPE.ONE_WAY ? 'OW' : 'RT',
    }),
  };

  usePageViewLog(selectedCategory ? logItem : null);

  const handleChangeCategory = (category: RailEuropeTrainCategoryType) => {
    setSelectedCategory(category);

    if (category === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET) {
      setSelectedJourney(prevSelectedJourney ?? JOURNEY_TYPE.ONE_WAY);
    }

    const newPath = replaceSearchQueries({
      queries: { ...queries, category },
      pathname,
      selectedCategory: category,
      selectedJourney:
        category === RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET
          ? prevSelectedJourney ?? JOURNEY_TYPE.ONE_WAY
          : null,
    });

    replace(newPath);
  };

  const handleChangeJourney = (journey: JourneyType) => {
    setSelectedJourney(journey);

    const newPath = replaceSearchQueries({
      queries: { ...queries, journey },
      pathname,
      selectedCategory,
      selectedJourney: journey,
    });

    replace(newPath);
  };

  useEffect(() => {
    const newPath = replaceSearchQueries({
      queries,
      pathname,
      selectedCategory,
      selectedJourney,
    });

    const newSearchParams = new URL(newPath, window.location.origin).searchParams;
    const currentSearchParams = new URLSearchParams();

    Object.entries(originQuery).forEach(([key, value]) => {
      if (value !== undefined) {
        currentSearchParams.append(key, value as string);
      }
    });

    const isSameParams =
      newSearchParams.size === currentSearchParams.size &&
      Array.from(newSearchParams.keys()).every(
        (key) => currentSearchParams.get(key) === newSearchParams.get(key),
      );

    if (!isSameParams) {
      replace(newPath);
    }
  }, [selectedCategory, selectedJourney, pathname, queries, originQuery, replace]);

  return (
    <RailEuropeSearchPage>
      <NavigationContainer ref={navigationContainerRef} isStuck={isStuck}>
        <SearchHeader />
        <SearchCategoryTab
          selectedCategory={selectedCategory}
          onChangeCategory={handleChangeCategory}
        />
      </NavigationContainer>
      <Tab.Content tabHeight={RAIL_EUROPE_TAB_HEIGHT}>
        <SwitchCase
          value={selectedCategory}
          caseBy={{
            [RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET]: selectedJourney && (
              <TicketTabContent
                selectedJourney={selectedJourney}
                onChangeJourney={handleChangeJourney}
              />
            ),
            [RAIL_EUROPE_TRAIN_CATEGORY_TYPE.PASS]: <PassTabContent />,
          }}
        />
      </Tab.Content>
    </RailEuropeSearchPage>
  );
};

const RailEuropeSearchPage = styled.section`
  ${media.minMobile} {
    margin-top: 100px;
  }
`;

const NavigationContainer = styled.nav<{ isStuck: boolean }>`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${HEADER_Z_INDEX + 1};
  background: ${({ theme }) => theme.colors.white};

  ${media.minMobile} {
    z-index: ${HEADER_Z_INDEX};
    ${({ isStuck }) =>
      isStuck &&
      css`
        margin: 0 -24px;
        padding: ${PC_SEARCH_HOME_SCROLL_PADDING_TOP}px 24px 0;
      `}
  }
`;

export default RailEuropeSearch;
